// @ts-strict-ignore
// eslint-disable-next-line no-restricted-imports
import {AttrsSchema, Component} from 'panel';
import React from 'react';
import pick from 'lodash/pick';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {Directions, PositioningStrategy} from 'common/components/drop-menu/types';
import {RevealableTextEditor} from 'common/components/revealable-text-editor';
import {IronContextRegistry} from 'common/report/context-registry';
import {propagateEvent} from 'common/util/dom';

// eslint-disable-next-line import/no-cycle
import {ContextPanel, ContextPanelAttrs, ContextPanelEventHandlers, ContextPanelParams} from '../index';
import css from './index.inline.styl';
import {template} from './template';

export type ContextPanelMenuAttrs = ContextPanelAttrs & {
  placement?: Directions;
  'force-placement'?: boolean;
  'position-strategy'?: PositioningStrategy;
  offset?: number;
  skidding?: number;
  'show-arrow'?: boolean;
  'first-class-metrics-enabled'?: boolean;
};

type ContextPanelMenuParams = ContextPanelParams & {
  positionParent?: HTMLElement;
};

type ContextPanelMenuEventHandlers = ContextPanelEventHandlers;

export type ContextPanelMenuProps = {
  params?: ContextPanelMenuParams;
  attrs?: ContextPanelMenuAttrs;
  on?: ContextPanelMenuEventHandlers;
};

type ContextPanelMenuState = EmptyObj;

export class ContextPanelMenu extends Component<
  ContextPanelMenuState,
  ContextPanelMenuAttrs,
  EmptyObj,
  EmptyObj,
  IronContextRegistry,
  ContextPanelMenuParams
> {
  private declare textEditor: RevealableTextEditor;

  static get attrsSchema(): AttrsSchema<ContextPanelMenuAttrs> {
    return {
      ...super.attrsSchema,
      ...ContextPanel.attrsSchema,
      placement: {
        type: `string`,
        default: Directions.Right,
        enum: Object.values(Directions),
      },
      'position-strategy': {
        type: `string`,
        enum: Object.values(PositioningStrategy),
        default: PositioningStrategy.Absolute,
        description: `Defines how the tooltip is rendered, fixed to the document level or absolute relative to its offset parent`,
      },
      'force-placement': `boolean`,
      offset: {type: `number`, default: 2},
      skidding: {type: `number`, default: 0},
      'show-arrow': {type: `boolean`, description: `Whether or not to show a tooltip arrow`},
      'first-class-metrics-enabled': `boolean`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      params: {
        ...ContextPanel.paramsSchema,
        positionParent: Object,
      },
      helpers: {
        isTooltipDataSet: (): boolean => {
          const tooltipData = this.params.data;
          return !!tooltipData && Object.values(tooltipData).some((v) => (Array.isArray(v) ? !!v.length : !!v));
        },
      },
    };
  }

  getContextPanelContentParams(): ContextPanelParams {
    return pick(this.params, Object.keys(ContextPanel.paramsSchema));
  }

  getContextPanelContentAttrs(): ContextPanelAttrs {
    return pick(this.attrs(), Object.keys(ContextPanel.attrsSchema));
  }

  getContextPanelContentEventHandlers(): ContextPanelEventHandlers {
    return {
      contextPanelHoverIn: (ev) => propagateEvent(this, ev),
      contextPanelHoverOut: (ev) => propagateEvent(this, ev),
      tooltipContextAction: (ev) => propagateEvent(this, ev),
      tooltipContextEditDescription: (ev) => propagateEvent(this, ev),
    };
  }
}

export const ContextPanelMenuEl = defineAndMakeJsxFactory<ContextPanelMenuProps>(
  `mp-context-panel-menu`,
  ContextPanelMenu,
);
export const ContextPanelMenuReact = panel2react<ContextPanelMenuProps>(`mp-context-panel-menu`, ContextPanelMenu);

export type ContextPanelMenuReactProps = React.ComponentProps<typeof ContextPanelMenuReact>;
